import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { canEditCustoms, canViewCustoms, type BrokerRequestDetailDto } from '@crm/shared'
import {
  deleteBrokerRequest,
  getBrokerRequest,
  setBrokerRequestBroker,
} from '../../../shared/api/customs.api'
import { apiErrorText } from '../../../shared/api/errorText'
import { useAuthStore } from '../../auth/authStore'
import { PageLoader } from '../../../shared/ui/Spinner'
import { EmptyState } from '../../../shared/ui/EmptyState'
import { ErrorMessage } from '../../../shared/ui/ErrorMessage'
import { confirm } from '../../../shared/ui/confirmStore'
import { toast } from '../../../shared/ui/toastStore'
import { BrokerGrid, gridHeightForRows } from './BrokerGrid'
import { BrokerRequestStatusBadge } from './BrokerStatusBadge'
import { BrokerUploadButton } from './BrokerUploadButton'
import {
  downloadBrokerAnswers,
  downloadBrokerFile,
  downloadBrokerRemainder,
  pendingBrokerItems,
  positionsWord,
} from './brokerActions'
import { BrokerSelect } from '../brokers/BrokerSelect'
import { BrokerCardModal } from '../brokers/BrokerCardModal'
import { useCustomsBrokers } from '../brokers/useCustomsBrokers'
import {
  buildItemColDefs,
  buildPositionColDefs,
  formatDateTimeRu,
  toItemRow,
  toPositionRow,
  type BrokerItemRow,
  type BrokerPositionRow,
} from './brokerColDefs'

const JOURNAL_PATH = '/customs/requests'

/**
 * Запрос брокеру (Л2): что ушло в файле (уникальные партномера), что ответил
 * брокер и на какие позиции заявок разложен ответ. Логист может скачать файл
 * ещё раз, загрузить ответ и отменить запрос.
 */
export function BrokerRequestDetailPage() {
  const role = useAuthStore((s) => s.user?.role)
  const { id } = useParams<{ id: string }>()
  if (!canViewCustoms(role)) {
    return (
      <EmptyState
        title="Раздел недоступен"
        description="Запросы брокеру видят логист, администратор, генеральный директор и РОЗ."
      />
    )
  }
  if (!id) return <EmptyState title="Запрос брокеру не найден" />
  return <BrokerRequestDetailView id={id} canEdit={canEditCustoms(role)} />
}

function Breadcrumb({ fileCode }: { fileCode?: string }) {
  return (
    <div className="text-sm text-gray-500">
      <Link to="/customs" className="hover:text-gray-900">
        Нетарифка
      </Link>
      <span className="mx-2">›</span>
      <Link to={JOURNAL_PATH} className="hover:text-gray-900">
        Запросы брокеру
      </Link>
      {fileCode && (
        <>
          <span className="mx-2">›</span>
          <span className="text-gray-900 font-mono">{fileCode}</span>
        </>
      )}
    </div>
  )
}

function LoadError({ error }: { error: unknown }) {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined
  return (
    <div className="space-y-4">
      <Breadcrumb />
      {status === 404 ? (
        <EmptyState
          title="Запрос брокеру не найден"
          description="Возможно, его уже отменили."
          action={
            <Link to={JOURNAL_PATH} className="text-brand-600 hover:text-brand-700 font-medium">
              ← К журналу запросов
            </Link>
          }
        />
      ) : status === 403 ? (
        <EmptyState title="Нет доступа" />
      ) : (
        <ErrorMessage error={error} />
      )}
    </div>
  )
}

function BrokerRequestDetailView({ id, canEdit }: { id: string; canEdit: boolean }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [downloading, setDownloading] = useState(false)
  const [search, setSearch] = useState('')

  const query = useQuery({
    queryKey: ['customs', 'broker-request', id],
    queryFn: () => getBrokerRequest(id),
    // Без WebSocket у логиста: перечитываем при открытии и возврате на вкладку.
    refetchOnWindowFocus: true,
    staleTime: 0,
  })
  const req = query.data

  // Поиск по партномеру (с 30.10.2026): без пробелов, дефисов и регистра — «x-cross» найдёт «X CROSS».
  const needle = partSearchKey(search)
  const itemRows = useMemo<BrokerItemRow[]>(
    () => (req?.items ?? []).filter((it) => partMatches(it.partNumber, needle)).map(toItemRow),
    [req, needle],
  )
  const positionRows = useMemo<BrokerPositionRow[]>(
    () =>
      (req?.positions ?? []).filter((p) => partMatches(p.partNumber, needle)).map(toPositionRow),
    [req, needle],
  )
  const itemColDefs = useMemo(() => buildItemColDefs(), [])
  const positionColDefs = useMemo(() => buildPositionColDefs(), [])

  const cancelMut = useMutation({
    mutationFn: () => deleteBrokerRequest(id),
    onSuccess: ({ reverted }) => {
      toast.success(
        reverted > 0
          ? `Запрос ${req?.fileCode ?? ''} отменён. Вернулось в очередь: ${reverted} ${positionsWord(reverted)}`
          : `Запрос ${req?.fileCode ?? ''} отменён`,
      )
      queryClient.removeQueries({ queryKey: ['customs', 'broker-request', id] })
      void queryClient.invalidateQueries({ queryKey: ['customs'] })
      void navigate(JOURNAL_PATH, { replace: true })
    },
    onError: (err) => toast.error(apiErrorText(err, 'Не удалось отменить запрос брокеру')),
  })

  if (query.isLoading) return <PageLoader label="Загружаю запрос брокеру…" />
  if (query.isError) return <LoadError error={query.error} />
  if (!req) return <EmptyState title="Запрос брокеру не найден" />

  const waiting = req.positions.filter((p) => p.status === 'SENT').length

  const onDownload = async (mode: DownloadMode = 'file') => {
    setDownloading(true)
    try {
      if (mode === 'remainder') {
        const count = await downloadBrokerRemainder(req)
        toast.success(`Скачан файл остатков: партномеров без ответа — ${count}`)
      } else if (mode === 'answers') {
        const count = await downloadBrokerAnswers(req)
        if (count === 0) toast.info('Ответов брокера по этому запросу ещё нет')
      } else {
        await downloadBrokerFile(req)
      }
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось собрать файл для брокера'))
    } finally {
      setDownloading(false)
    }
  }

  const onCancel = async () => {
    const ok = await confirm({
      title: `Отменить запрос ${req.fileCode}?`,
      message:
        (waiting > 0
          ? `Позиции без ответа брокера (${waiting}) вернутся в статус «В очереди». `
          : '') + 'Уже полученные ответы останутся в позициях. Запрос исчезнет из журнала.',
      confirmText: 'Отменить запрос',
      cancelText: 'Не отменять',
      danger: true,
    })
    if (ok) cancelMut.mutate()
  }

  return (
    <div className="space-y-4">
      <Breadcrumb fileCode={req.fileCode} />
      <HeaderCard
        req={req}
        canEdit={canEdit}
        downloading={downloading}
        cancelling={cancelMut.isPending}
        onDownload={(mode) => void onDownload(mode)}
        onCancel={() => void onCancel()}
      />

      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Поиск по партномеру"
        aria-label="Поиск по партномеру"
        className="w-full sm:w-80 px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
      />

      <section className="space-y-2">
        <h2 className="text-base font-semibold text-gray-900">
          Партномера в запросе <span className="text-sm text-gray-500">({itemRows.length})</span>
        </h2>
        <BrokerGrid<BrokerItemRow>
          rowData={itemRows}
          columnDefs={itemColDefs}
          storageKey="crm_customs_broker_items_cols_v1"
          className=""
          style={gridHeightForRows(itemRows.length)}
          noRowsText="В запросе нет партномеров"
        />
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-semibold text-gray-900">
          Позиции заявок <span className="text-sm text-gray-500">({positionRows.length})</span>
        </h2>
        <p className="text-xs text-gray-500">
          Ответ по партномеру раскладывается на все его позиции из запроса. Позиций удалённых заявок
          здесь нет.
        </p>
        <BrokerGrid<BrokerPositionRow>
          rowData={positionRows}
          columnDefs={positionColDefs}
          storageKey="crm_customs_broker_positions_cols_v1"
          className=""
          style={gridHeightForRows(positionRows.length)}
          noRowsText="Нет позиций заявок"
        />
      </section>
    </div>
  )
}

/** Ключ поиска партномера: только буквы и цифры, верхний регистр. */
function partSearchKey(s: string): string {
  return s.toUpperCase().replace(/[^0-9A-ZА-ЯЁ]/g, '')
}

function partMatches(partNumber: string, needle: string): boolean {
  return !needle || partSearchKey(partNumber).includes(needle)
}

/** Файл запроса, остатки (партномера без ответа) или файл с записанными ответами. */
type DownloadMode = 'file' | 'remainder' | 'answers'

interface HeaderCardProps {
  req: BrokerRequestDetailDto
  canEdit: boolean
  downloading: boolean
  cancelling: boolean
  onDownload: (mode?: DownloadMode) => void
  onCancel: () => void
}

/**
 * Брокер запроса: у логиста — выбор (указать у старого запроса, исправить
 * ошибку), у остальных — имя; по клику — карточка брокера с контактами.
 */
function BrokerFact({ req, canEdit }: { req: BrokerRequestDetailDto; canEdit: boolean }) {
  const qc = useQueryClient()
  const brokers = useCustomsBrokers(true)
  const [cardOpen, setCardOpen] = useState(false)
  const card = brokers.data?.find((b) => b.id === req.broker?.id)
  const change = useMutation({
    mutationFn: (brokerId: string | null) => setBrokerRequestBroker(req.id, brokerId),
    onSuccess: (detail) => {
      qc.setQueryData(['customs', 'broker-request', req.id], detail)
      void qc.invalidateQueries({ queryKey: ['customs', 'broker-requests'] })
      void qc.invalidateQueries({ queryKey: ['customs', 'brokers'] })
      toast.success(detail.broker ? `Брокер: ${detail.broker.name}` : 'Брокер снят')
    },
    onError: (err) => toast.error(apiErrorText(err, 'Не удалось сменить брокера')),
  })
  return (
    <div className="min-w-0">
      <span className="block text-xs text-gray-500">Брокер</span>
      {canEdit ? (
        <div className="max-w-[260px]">
          <BrokerSelect
            value={req.broker?.id ?? null}
            current={req.broker}
            disabled={change.isPending}
            onChange={(id) => change.mutate(id)}
          />
        </div>
      ) : req.broker ? (
        <button
          type="button"
          onClick={() => setCardOpen(true)}
          className="text-brand-600 hover:text-brand-700 text-left"
        >
          {req.broker.name}
        </button>
      ) : (
        <span className="text-gray-900">не указан</span>
      )}
      {canEdit && card && (
        <button
          type="button"
          onClick={() => setCardOpen(true)}
          className="mt-1 text-xs text-brand-600 hover:text-brand-700"
        >
          Контакты брокера
        </button>
      )}
      {cardOpen && card && (
        <BrokerCardModal broker={card} readOnly onClose={() => setCardOpen(false)} />
      )}
    </div>
  )
}

function HeaderCard({
  req,
  canEdit,
  downloading,
  cancelling,
  onDownload,
  onCancel,
}: HeaderCardProps) {
  const facts: Array<{ label: string; value: string }> = [
    { label: 'Создан', value: formatDateTimeRu(req.createdAt) },
    { label: 'Ответ получен', value: formatDateTimeRu(req.answeredAt) },
    { label: 'Логист', value: req.logist?.name ?? '—' },
    { label: 'Партномеров с ответом', value: `${req.answeredCount} из ${req.itemsCount}` },
    { label: 'Позиций заявок', value: String(req.positions.length) },
  ]
  const pending = pendingBrokerItems(req).length
  // Остатки — когда брокер ответил не на всё: файл только с партномерами без ответа.
  const showRemainder = pending > 0 && pending < req.items.length
  const answersButton =
    req.answeredCount > 0 ? (
      <button
        type="button"
        onClick={() => onDownload('answers')}
        disabled={downloading}
        title="Тот же файл, колонки брокера заполнены записанными ответами"
        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50 transition-colors text-gray-700 disabled:opacity-50"
      >
        Скачать с ответами
      </button>
    ) : null
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-xl font-semibold text-gray-900 font-mono">{req.fileCode}</h1>
        <BrokerRequestStatusBadge status={req.status} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
        <BrokerFact req={req} canEdit={canEdit} />
        {facts.map((f) => (
          <div key={f.label}>
            <span className="block text-xs text-gray-500">{f.label}</span>
            <span className="text-gray-900">{f.value}</span>
          </div>
        ))}
      </div>
      {req.note && (
        <div className="text-sm">
          <span className="block text-xs text-gray-500">Примечание</span>
          <span className="text-gray-700 whitespace-pre-wrap">{req.note}</span>
        </div>
      )}

      {canEdit ? (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button
            type="button"
            onClick={() => onDownload()}
            disabled={downloading}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50 transition-colors text-gray-700 disabled:opacity-50"
          >
            {downloading ? 'Собираю файл…' : 'Скачать файл ещё раз'}
          </button>
          {showRemainder && (
            <button
              type="button"
              onClick={() => onDownload('remainder')}
              disabled={downloading}
              title="Файл только с партномерами, по которым брокер ещё не ответил. Ответ на него загружается в этот же запрос"
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50 transition-colors text-gray-700 disabled:opacity-50"
            >
              Скачать остатки ({pending})
            </button>
          )}
          {answersButton}
          <BrokerUploadButton
            brokerRequestId={req.id}
            className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
          />
          <div className="flex-1" />
          <button
            type="button"
            onClick={onCancel}
            disabled={cancelling}
            className="px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
          >
            {cancelling ? 'Отмена…' : 'Отменить запрос'}
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3 pt-1">
          {answersButton}
          <p className="text-xs text-gray-500">Просмотр: правка доступна логисту</p>
        </div>
      )}
    </div>
  )
}
